import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import { apiPost, ApiError } from '@shared/libs/apiClient'
import { CONTACT } from './data/wp-structured'

type ContactForm = {
    name: string
    email: string
    subject: string
    message: string
}

const EDITORIAL_EMAIL = 'editorial@cspgindia.in'

export default function Contact() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<ContactForm>()
    const [sent, setSent] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const onSubmit = async (values: ContactForm) => {
        setError(null)
        try {
            await apiPost<{ success: boolean; message: string }>('/site/contact', values)
            setSent(true)
            reset()
        } catch (err) {
            const message =
                err instanceof ApiError
                    ? err.message
                    : `We could not send your message. Please write to ${EDITORIAL_EMAIL} directly.`
            setError(message)
            const subject = encodeURIComponent(values.subject)
            const body = encodeURIComponent(`From: ${values.name} <${values.email}>\n\n${values.message}`)
            window.location.href = `mailto:${EDITORIAL_EMAIL}?subject=${subject}&body=${body}`
        }
    }

    return (
        <>
            <SEO
                title="Contact"
                description="Editorial office contact details for Indian Studies Review and the Centre for Study of Politics and Governance."
                path="/contact-us"
            />
            <PageHeader
                eyebrow="Contact"
                title="Get in Touch"
                lede="For editorial enquiries, submissions support, or matters concerning the Centre."
            />

            <section className="section">
                <div className="container-prose grid md:grid-cols-12 gap-x-12 gap-y-16">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="md:col-span-5"
                    >
                        <div className="eyebrow">Editorial Office</div>
                        <h2 className="section-title mt-5">
                            We <em>read</em> every message
                        </h2>
                        <p className="prose-body mt-6">
                            The editorial office handles author enquiries, board nominations, partnership
                            proposals, and press requests. Routine submissions support is routed to the
                            relevant editor; you can expect a substantive response within five working days.
                        </p>

                        {CONTACT && (() => {
                            const contact = CONTACT
                            return (
                            <>
                                <div
                                    className="mt-10 pt-6"
                                    style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                                >
                                    <div
                                        className="font-display"
                                        style={{
                                            fontSize: '1.15rem',
                                            color: 'var(--color-ink-700)',
                                            letterSpacing: '-0.012em',
                                            fontWeight: 500
                                        }}
                                    >
                                        {contact.name}
                                    </div>
                                    {contact.role && (
                                        <div
                                            className="mt-1.5 text-[11px] uppercase font-semibold"
                                            style={{ letterSpacing: '0.22em', color: 'var(--color-gold-700)' }}
                                        >
                                            {contact.role}
                                        </div>
                                    )}
                                    {contact.address && (
                                        <address
                                            className="not-italic mt-4 text-[14.5px] leading-relaxed whitespace-pre-line"
                                            style={{ color: 'var(--color-ink-500)' }}
                                        >
                                            {contact.address}
                                        </address>
                                    )}
                                </div>

                                <dl
                                    className="mt-8"
                                    style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                                >
                                    {contact.emails.map((email, i, arr) => (
                                        <div
                                            key={email}
                                            className="grid grid-cols-[6rem_1fr] items-baseline py-3.5"
                                            style={{
                                                borderBottom:
                                                    i < arr.length - 1 || contact.phone
                                                        ? '1px solid var(--color-rule)'
                                                        : '1px solid var(--color-rule-strong)'
                                            }}
                                        >
                                            <dt
                                                className="text-[10.5px] uppercase font-semibold"
                                                style={{ letterSpacing: '0.22em', color: 'var(--color-ink-400)' }}
                                            >
                                                Email
                                            </dt>
                                            <dd
                                                className="font-serif text-[15.5px]"
                                                style={{ color: 'var(--color-gold-700)' }}
                                            >
                                                <a href={`mailto:${email}`}>{email}</a>
                                            </dd>
                                        </div>
                                    ))}
                                    {contact.phone && (
                                        <div
                                            className="grid grid-cols-[6rem_1fr] items-baseline py-3.5"
                                            style={{ borderBottom: '1px solid var(--color-rule-strong)' }}
                                        >
                                            <dt
                                                className="text-[10.5px] uppercase font-semibold"
                                                style={{ letterSpacing: '0.22em', color: 'var(--color-ink-400)' }}
                                            >
                                                Phone
                                            </dt>
                                            <dd
                                                className="font-serif text-[15.5px]"
                                                style={{ color: 'var(--color-ink-600)' }}
                                            >
                                                <a href={`tel:${contact.phone.replace(/\s+/g, '')}`}>{contact.phone}</a>
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </>
                            )
                        })()}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="md:col-span-7 md:pl-10"
                        style={{ borderLeft: '1px solid var(--color-rule)' }}
                    >
                        <div className="eyebrow">Send a message</div>

                        {sent ? (
                            <div className="mt-8 pt-10" style={{ borderTop: '1px solid var(--color-rule-strong)' }}>
                                <h3
                                    className="font-display italic"
                                    style={{
                                        fontSize: 'clamp(1.625rem, 3vw, 2.25rem)',
                                        lineHeight: 1.2,
                                        color: 'var(--color-ink-700)',
                                        letterSpacing: '-0.018em'
                                    }}
                                >
                                    Thank you. We’ll respond within five working days
                                </h3>
                                <p
                                    className="mt-6 text-[15.5px] leading-relaxed"
                                    style={{ color: 'var(--color-ink-400)' }}
                                >
                                    Your message has been routed to the editorial office. If your enquiry is
                                    time-sensitive, you may also reach us by email at editorial@cspgindia.in.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-ghost mt-8"
                                    onClick={() => setSent(false)}
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form
                                className="mt-8"
                                onSubmit={handleSubmit(onSubmit)}
                                noValidate
                                style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                            >
                                <div
                                    className="py-6"
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <label className="label" htmlFor="name">
                                        Name
                                    </label>
                                    <input
                                        id="name"
                                        className="input"
                                        autoComplete="name"
                                        {...register('name', { required: 'Your name is required' })}
                                    />
                                    {errors.name && (
                                        <p className="text-xs mt-2" style={{ color: '#b91c1c' }}>
                                            {errors.name.message}
                                        </p>
                                    )}
                                </div>

                                <div
                                    className="py-6"
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <label className="label" htmlFor="email">
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        className="input"
                                        autoComplete="email"
                                        {...register('email', {
                                            required: 'An email address is required',
                                            pattern: {
                                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                message: 'Please enter a valid email address'
                                            }
                                        })}
                                    />
                                    {errors.email && (
                                        <p className="text-xs mt-2" style={{ color: '#b91c1c' }}>
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>

                                <div
                                    className="py-6"
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <label className="label" htmlFor="subject">
                                        Subject
                                    </label>
                                    <input
                                        id="subject"
                                        className="input"
                                        {...register('subject', { required: 'A subject is required' })}
                                    />
                                    {errors.subject && (
                                        <p className="text-xs mt-2" style={{ color: '#b91c1c' }}>
                                            {errors.subject.message}
                                        </p>
                                    )}
                                </div>

                                <div
                                    className="py-6"
                                    style={{ borderBottom: '1px solid var(--color-rule-strong)' }}
                                >
                                    <label className="label" htmlFor="message">
                                        Message
                                    </label>
                                    <textarea
                                        id="message"
                                        rows={6}
                                        className="input"
                                        {...register('message', {
                                            required: 'Please include a message',
                                            minLength: { value: 20, message: 'Please write at least 20 characters' }
                                        })}
                                    />
                                    {errors.message && (
                                        <p className="text-xs mt-2" style={{ color: '#b91c1c' }}>
                                            {errors.message.message}
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <p className="text-sm mt-5" style={{ color: '#b91c1c' }}>
                                        {error}
                                    </p>
                                )}

                                <div className="mt-8">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Sending…' : 'Send message'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </section>
        </>
    )
}
